import {Box} from '@dropbox/dig-foundations';
import {useDcdOwnershipSearchAnalytics} from 'analytics/useDcdOwnershipSearchAnalytics';
import type {DcdOwnershipComponentSummary} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {
  getComponentsAndServiceReferenceRoute,
  getComponentsAndServicesServiceRoute,
} from 'constant';
import {useEffect, useMemo, useRef} from 'react';
import {useNavigate} from 'react-router';

const matchingServices = (component: DcdOwnershipComponentSummary, search: string) =>
  (component.services ?? []).filter(
    ({name, description}) =>
      name.toLocaleLowerCase() === search || description?.toLocaleLowerCase().includes(search)
  );

const matchesSearch = (component: DcdOwnershipComponentSummary, search: string) =>
  component.name.toLocaleLowerCase().includes(search) ||
  matchingServices(component, search).length > 0;

// A search match (a service or reference) sits one level in, under its component.
const MATCH_INDENT_PX = 20;

export const OwnershipComponentList = ({
  components,
  searchQuery = '',
  useServerSearch = false,
  showServerSearchMatches = false,
  isSearchLoading = false,
  isSearchError = false,
  activeComponentId,
  onNavigate,
  bleed,
}: {
  components: DcdOwnershipComponentSummary[];
  searchQuery?: string;
  useServerSearch?: boolean;
  showServerSearchMatches?: boolean;
  isSearchLoading?: boolean;
  isSearchError?: boolean;
  activeComponentId?: number;
  onNavigate?: () => void;
  // Full-bleed rows in the page rail; rounded rows in the narrow screens' nav.
  bleed?: {left: number; right: number};
}) => {
  const navigate = useNavigate();
  const selectedRef = useRef<HTMLDivElement>(null);
  const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
  const hasSearchQuery = normalizedSearch.length > 0;

  const visibleComponents = useMemo(() => {
    if (!hasSearchQuery || useServerSearch) {
      return components;
    }

    return components.filter((component) => matchesSearch(component, normalizedSearch));
  }, [components, hasSearchQuery, normalizedSearch, useServerSearch]);
  const {recordSelection} = useDcdOwnershipSearchAnalytics({
    query: searchQuery,
    searchSurface: 'components_and_services',
    useServerSearch,
    resultCount: visibleComponents.length,
    isLoading: isSearchLoading,
    isError: isSearchError,
  });

  useEffect(() => {
    if (hasSearchQuery) {
      return;
    }

    selectedRef.current?.scrollIntoView({
      block: 'center',
      inline: 'nearest',
    });
  }, [activeComponentId, hasSearchQuery, visibleComponents]);

  // Records the pick, clears the search, then opens the page.
  const open = (href: string, selection: Parameters<typeof recordSelection>[0]) => {
    recordSelection(selection);
    onNavigate?.();
    navigate(href);
  };

  return (
    <Box style={bleed ? {marginLeft: -bleed.left, marginRight: -bleed.right} : undefined}>
      {visibleComponents.map((component, componentRank) => {
        const isSelected = activeComponentId === component.id;
        const componentHref = getComponentsAndServiceReferenceRoute(component.id);
        const services = showServerSearchMatches
          ? (component.services ?? [])
          : matchingServices(component, normalizedSearch);
        const references = showServerSearchMatches ? (component.references ?? []) : [];

        return (
          <div key={component.id} ref={isSelected ? selectedRef : undefined}>
            <Layout.RailTreeRow
              label={component.name}
              selected={isSelected}
              href={componentHref}
              onActivate={() => open(componentHref, {resultType: 'component', rank: componentRank})}
              bleed={bleed}
            />
            {hasSearchQuery &&
              (services.length > 0
                ? services.map((service, serviceRank) => {
                    const href = getComponentsAndServicesServiceRoute(component.id, service.id);
                    return (
                      <Layout.RailTreeRow
                        key={service.id}
                        label={service.name}
                        indentPx={MATCH_INDENT_PX}
                        truncateLocation={0.5}
                        href={href}
                        onActivate={() => open(href, {resultType: 'service', rank: serviceRank})}
                        bleed={bleed}
                      />
                    );
                  })
                : references.map((reference, referenceRank) => {
                    const href = getComponentsAndServiceReferenceRoute(component.id, reference.id);
                    return (
                      <Layout.RailTreeRow
                        key={reference.id}
                        label={reference.name}
                        indentPx={MATCH_INDENT_PX}
                        truncateLocation={0.5}
                        subtleLabel
                        href={href}
                        onActivate={() =>
                          open(href, {resultType: reference.reference_type, rank: referenceRank})
                        }
                        bleed={bleed}
                      />
                    );
                  }))}
          </div>
        );
      })}
    </Box>
  );
};
